import { SidebarActionReveal } from '@affine/core/modules/app-sidebar/views';
import clsx from 'clsx';
import {
  type ButtonHTMLAttributes,
  forwardRef,
  type MouseEventHandler,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react';

import * as styles from './sidebar-account-item.css';

export const NativeWorkspaceAppSidebarActionButton = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement>
>(function NativeWorkspaceAppSidebarActionButton(
  { className, type = 'button', ...props },
  ref
) {
  return (
    <button
      {...props}
      ref={ref}
      className={clsx(styles.actionButton, className)}
      type={type}
    />
  );
});

export interface NativeWorkspaceAppSidebarAccountItemProps {
  actions?: ReactNode;
  active?: boolean;
  avatar: ReactNode;
  avatarAction?: ReactNode;
  avatarClassName?: string;
  avatarVariant?: 'avatar' | 'providerLogo';
  className?: string;
  disabled?: boolean;
  dragHandleRef?: Ref<HTMLDivElement>;
  expanded?: boolean;
  editingTitle?: ReactNode;
  meta?: ReactNode;
  onClick: MouseEventHandler<HTMLButtonElement>;
  selected?: boolean;
  status?: ReactNode;
  testId?: string;
  title: ReactNode;
}

/** Shared top-level account/source row chrome for native workspace apps. */
export function NativeWorkspaceAppSidebarAccountItem({
  actions,
  active = false,
  avatar,
  avatarAction,
  avatarClassName,
  avatarVariant = 'avatar',
  className,
  disabled = false,
  dragHandleRef,
  expanded,
  editingTitle,
  meta,
  onClick,
  selected = false,
  status,
  testId,
  title,
}: NativeWorkspaceAppSidebarAccountItemProps): ReactElement {
  return (
    <SidebarActionReveal
      actions={actions}
      actionsClassName={styles.actions}
      className={clsx(styles.root, className)}
      contentClassName={styles.content}
      data-active={active}
      data-selected={selected}
      data-sidebar-section-snap-target="true"
      ref={dragHandleRef}
    >
      {editingTitle ? (
        <div className={styles.button} data-testid={testId}>
          <span
            className={clsx(
              styles.avatar,
              avatarVariant === 'providerLogo' && styles.providerLogoAvatar,
              avatarClassName
            )}
            aria-hidden="true"
          >
            {avatarAction ? null : avatar}
          </span>
          <span className={styles.text}>
            <span className={styles.title}>{editingTitle}</span>
            {meta ? <span className={styles.meta}>{meta}</span> : null}
          </span>
          {status ? (
            <span className={styles.status} data-sidebar-action-reveal-fade>
              {status}
            </span>
          ) : null}
        </div>
      ) : (
        <button
          aria-current={selected ? 'true' : undefined}
          aria-expanded={expanded}
          className={styles.button}
          data-testid={testId}
          disabled={disabled}
          type="button"
          onClick={onClick}
        >
          <span
            className={clsx(
              styles.avatar,
              avatarVariant === 'providerLogo' && styles.providerLogoAvatar,
              avatarClassName
            )}
            aria-hidden="true"
          >
            {avatarAction ? null : avatar}
          </span>
          <span className={styles.text}>
            <span className={styles.title}>{title}</span>
            {meta ? <span className={styles.meta}>{meta}</span> : null}
          </span>
          {status ? (
            <span className={styles.status} data-sidebar-action-reveal-fade>
              {status}
            </span>
          ) : null}
        </button>
      )}
      {avatarAction ? (
        <span className={styles.avatarAction}>{avatarAction}</span>
      ) : null}
    </SidebarActionReveal>
  );
}
